import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ProjectsService } from '../../core/services/projects.service';
import { Project } from '../../core/models/project.model';
import { CurrencyService } from '../../core/services/currency.service';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { LucideIconsModule } from '../../shared/lucide-icons.module';

@Component({
    selector: 'app-projects-page',
    standalone: true,
    imports: [CommonModule, RouterLink, LoadingSpinnerComponent, LucideIconsModule],
    templateUrl: './projects-page.component.html',
    styleUrls: ['./projects-page.component.scss'],
})
export class ProjectsPageComponent implements OnInit {
    private projectsService = inject(ProjectsService);
    private currencyService = inject(CurrencyService);

    projects: Project[] = [];
    loading = true;
    error: string | null = null;
    projectImageFailed: Record<string, boolean> = {};
    partnerLogoFailed: Record<string, boolean> = {};
    currentPage = 1;
    pageSize = 10;
    loadingPage = false;
    hasNextPage = true;
    totalCount = 0;

    ngOnInit() {
        this.fetchProjects();
    }

    fetchProjects(): void {
        const isFirstPage = this.currentPage === 1;
        this.loading = isFirstPage;
        this.loadingPage = true;
        this.error = null;
        this.projectsService.getProjects({ page: this.currentPage, perPage: this.pageSize }).subscribe({
            next: ({ items, total, page, perPage }) => {
                const existingIds = new Set(this.projects.map((p) => p.id));
                const merged = isFirstPage ? items : [...this.projects, ...items.filter((p) => !existingIds.has(p.id))];
                this.projects = merged;
                this.totalCount = total;
                const serverHasNext = total > page * perPage;
                this.hasNextPage = serverHasNext || items.length >= perPage;
                this.loading = false;
                this.loadingPage = false;
            },
            error: (err) => {
                this.projects = [];
                this.totalCount = 0;
                this.loading = false;
                this.loadingPage = false;
                this.error = err?.message || 'Unable to fetch projects';
            }
        });
    }

    loadMore() {
        if (!this.hasNextPage || this.loadingPage) return;
        this.currentPage += 1;
        this.fetchProjects();
    }

    statusLabel(status?: string | null): string {
        if (!status) return 'Active';
        return status.toString().replace(/_/g, ' ') || 'Active';
    }

    developerName(project: Project): string | null {
        const name = project.organizationName || project.vendorName || project.creatorName || null;
        if (!name) return null;
        const normalized = name.trim().toLowerCase();
        if (normalized.includes('nibni support')) {
            return null;
        }
        return name;
    }

    partnerDisplayName(project: Project): string | null {
        return this.developerName(project);
    }

    startingPriceLabel(project: Project): string | null {
        const value = project?.startingPrice;
        if (value === null || value === undefined || !Number.isFinite(value)) return null;
        const fromCode = project?.startingCurrency || 'EGP';
        const converted = this.currencyService.convert(value, fromCode);
        const currencyCode = this.currencyService.currentCurrency();
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currencyCode,
                currencyDisplay: 'code',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(converted).replace(/[\u00A0\u202F]/g, ' ');
        } catch {
            const amount = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(converted);
            return `${currencyCode} ${amount}`;
        }
    }

    onProjectImageError(projectId: string | null) {
        if (!projectId) return;
        this.projectImageFailed = { ...this.projectImageFailed, [projectId]: true };
    }

    onPartnerLogoError(projectId: string | null) {
        if (!projectId) return;
        this.partnerLogoFailed = { ...this.partnerLogoFailed, [projectId]: true };
    }

    private countryNameFromCode(code: string | null | undefined): string | null {
        const normalized = String(code || '').trim().toUpperCase();
        if (!/^[A-Z]{2}$/.test(normalized)) return null;
        try {
            const display = new Intl.DisplayNames(['en'], { type: 'region' });
            return display.of(normalized) || normalized;
        } catch {
            return normalized;
        }
    }

    projectCountryLabel(project: Project): string | null {
        const raw = (project?.country || '').toString().trim();
        if (!raw) return null;
        const upper = raw.toUpperCase();
        if (/^[A-Z]{2}$/.test(upper)) return this.countryNameFromCode(upper) || raw;
        return raw;
    }

    projectLocationLabel(project: Project): string | null {
        const raw = (
            project?.locationAddress ||
            project?.locationLabel ||
            project?.location ||
            project?.country ||
            ''
        ).toString().trim();
        return raw ? raw : null;
    }

    projectLocationLink(project: Project, label?: string | null): string {
        const direct = String(project?.locationLink || '').trim();
        if (direct && /^https?:\/\//i.test(direct)) return direct;
        return this.googleMapsSearchUrl(label || this.projectLocationLabel(project) || 'nibni');
    }

    googleMapsSearchUrl(location: string | null | undefined): string {
        const label = String(location || '').trim();
        const query = encodeURIComponent(label || 'nibni');
        return `https://www.google.com/maps/search/?api=1&query=${query}`;
    }
}
